import React, { useCallback, useEffect, useState } from "react";
import Routes from "./Routes";
import GlobalStyle from "./globalStyles";
import { Auth } from "aws-amplify";
import { get, isNull } from "lodash";
import {
  ApolloProvider,
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  createHttpLink,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { createAuthLink } from "aws-appsync-auth-link";
import cognitoConfig from "./cognitoConfig";
import { getUserPool } from "./graphQL/queries/companySetUp";
import "@progress/kendo-theme-material/dist/all.css";
import "../src/globalStyles/kendoStyles.css";
import { ThemeProvider } from "@material-ui/styles";
import { ToastWidget } from "./components/widgets/ToastWidget";
import selectedTheme from "./styles";
import { createAppSyncApolloClient } from "./subscriptionConnection/createAppSyncApolloClient";
import LogRocket from "logrocket";

const App = () => {
  if (process.env.REACT_APP_LOGROCKET) {
    LogRocket.init(process.env.REACT_APP_LOGROCKET);
  }
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [cognitoUser, setCognitoUser] = useState({});
  const [passwordPolicy, setPasswordPolicy] = useState("MEDIUM");
  const [companyTheme, setCompanyTheme] = useState("CLASSIC");
  const url = process.env.REACT_APP_GRAPHQL_ENDPOINT;
  const region = process.env.REACT_APP_GRAPHQL_REGION;
  const [client, setClient] = useState({});
  const httpLink = createHttpLink({
    uri: process.env.REACT_APP_GRAPHQL_ENDPOINT,
  });
  const verification = {
    url,
    region,
    auth: {
      type: "AMAZON_COGNITO_USER_POOLS",
      jwtToken: async () => (await Auth.currentSession()).getIdToken(),
    },
    disableOffline: true,
  };

  const link = ApolloLink.from([createAuthLink(verification)]);

  const createAppSyncHybrid = async () => {
    const getJwtToken = async () =>
      (await Auth.currentSession()).getIdToken().getJwtToken();
    const linkData = {
      appSyncApiUrl: url,
      getJwtToken,
      cacheConfig: new InMemoryCache(),
    };
    const cl = await createAppSyncApolloClient(linkData);
    setClient(cl);
  };

  const generalLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        "x-api-key": process.env.REACT_APP_API_KEY,
      },
    };
  });

  const publicClient = useCallback(() => {
    return new ApolloClient({
      link: generalLink.concat(httpLink),
      cache: new InMemoryCache(),
    });
  },[]);

  const initializeAWSConfig = useCallback(async (tenantId) => {
    if (tenantId || localStorage.getItem("tenantId")) {
      if (!tenantId) setIsLoading(true);

      try {
        const res = await publicClient().query({
          query: getUserPool,
          variables: {
            input: {
              id: tenantId || localStorage.getItem("tenantId"),
            },
          },
        });
        const passwordComplexity = get(
          res,
          "data.getCompanyUserpool.passwordComplexity",
          "MEDIUM"
        );
        setPasswordPolicy(passwordComplexity);
        localStorage.setItem("passwordComplexity", passwordComplexity);

        const themeDetails = get(
          res,
          "data.getCompanyUserpool.theme",
          "CLASSIC"
        );
        const themes = ["CLASSIC", "LIGHT", "DARK"];
        setCompanyTheme(
          themes.includes(themeDetails) ? themeDetails : "CLASSIC"
        );

        cognitoConfig.userPoolId = get(
          res,
          "data.getCompanyUserpool.userpoolDetails.userPoolId"
        );
        cognitoConfig.userPoolWebClientId = get(
          res,
          "data.getCompanyUserpool.userpoolDetails.clientId"
        );
        cognitoConfig.authenticationFlowType = "USER_PASSWORD_AUTH";
        Auth.configure(cognitoConfig);
      } catch (error) {
        console.log("error", error);
        return Promise.reject(error);
      }
    }
  },[]);
  const checkUser = useCallback(async () => {
    try {
      await initializeAWSConfig();
      await Auth.currentSession();
      const cognitoAuthUser = await Auth.currentAuthenticatedUser();
      setCognitoUser({ ...cognitoAuthUser });
      createAppSyncHybrid();
      setIsAuthenticated(cognitoAuthUser.username !== null);
      localStorage.removeItem("passwordComplexity");
      setClient(
        new ApolloClient({
          link,
          aws_user_pools_web_client_id: get(Auth, "userPool.clientId"),
          cache: new InMemoryCache(),
        })
      );
    } catch (error) {
      setIsLoading(false);
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    checkUser();
    // clear on location change
    // return history.listen(resetHidePanel);
  }, []);

  return (
    // <ErrorBoundary
    //   FallbackComponent={ErrorFallback}
    //   onReset={() => {
    //     // reset the state of your app so the error doesn't happen again
    //   }}>
    <ThemeProvider theme={selectedTheme(companyTheme)}>
      <div>
        <GlobalStyle theme={companyTheme} />
        <ToastWidget />
        <ApolloProvider client={client}>
          {!isNull(isAuthenticated) && (
            <Routes
              {...cognitoUser}
              isAuthenticated={isAuthenticated}
              passwordPolicy={passwordPolicy}
              isLoading={isLoading}
              checkUser={checkUser}
              initializeAWSConfig={initializeAWSConfig}
              publicClient={publicClient}
            />
          )}
        </ApolloProvider>
      </div>
    </ThemeProvider>
    // </ErrorBoundary>
  );
};

export default App;
